import { useEffect, useMemo, useState } from 'react';
import { Stack, Typography, Theme, Box } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FormHeader } from '../FormHeader';
import { BackButton } from 'components';

import { SentFaxes } from './components/SentFaxes';
import { ReceivedFaxes } from './components/ReceivedFaxes';
import ViewAgenda from '@mui/icons-material/ViewAgenda';
import ViewModule from '@mui/icons-material/ViewModule';

import styles from './FaxSettings.module.scss';

type Props = {
  onBackToSettingsClick(): void;
  onFaxSent?: () => void;
};

type Tab = 'received' | 'sent';

const VIEW_MODE_KEY = 'faxViewMode';
type ViewMode = 'tabs' | 'stacked';

export function FaxSettings({ onBackToSettingsClick, onFaxSent }: Props) {
  const { t } = useTranslation('settings');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [tab, setTab] = useState<Tab>('received');
  const [viewMode, setViewMode] = useState<ViewMode>('tabs');

  /* =====================
     Persisted View Mode
  ===================== */

  useEffect(() => {
    const stored = localStorage.getItem(VIEW_MODE_KEY) as ViewMode | null;
    if (stored) setViewMode(stored);
  }, []);

  const toggleViewMode = () => {
    setViewMode((prev) => {
      const next = prev === 'tabs' ? 'stacked' : 'tabs';
      localStorage.setItem(VIEW_MODE_KEY, next);
      return next;
    });
  };

  /* =====================
     Header Tabs (Tabs Mode)
  ===================== */

  const headerItems = useMemo(
    () => [
      {
        text: t('faxSettings.receivedFaxesUpper'),
        isSelected: tab === 'received',
        onClick: () => setTab('received'),
      },
      {
        text: t('faxSettings.sentFaxesUpper'),
        isSelected: tab === 'sent',
        onClick: () => setTab('sent'),
      },
    ],
    [tab, t],
  );

  /* =====================
     Render
  ===================== */

  return (
    <>
      {isMobile && <BackButton text={t('faxSettings.backToSettings')} onClick={onBackToSettingsClick} />}

      <div className={styles.container}>
        {/* ===== TABS MODE ===== */}
        {viewMode === 'tabs' && (
          <>
            <div className={styles.tabHeaderRow}>
              <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ width: '100%' }}>
                <FormHeader items={headerItems} />

                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={0.75}
                  onClick={toggleViewMode}
                  sx={{
                    cursor: 'pointer',
                    color: 'primary.main',
                    userSelect: 'none',
                  }}
                >
                  <ViewAgenda fontSize='small' />
                  <Typography variant='body2' fontWeight={500}>
                    {t('faxSettings.stackView')}
                  </Typography>
                </Stack>
              </Stack>
            </div>

            <div className={styles.main}>
              <Box sx={{ display: tab === 'received' ? 'block' : 'none' }}>
                <ReceivedFaxes onFaxSent={() => setTab('sent')} />
              </Box>
              <Box sx={{ display: tab === 'sent' ? 'block' : 'none' }}>
                <SentFaxes />
              </Box>
            </div>
          </>
        )}

        {/* ===== STACKED MODE ===== */}
        {viewMode === 'stacked' && (
          <>
            {/* ===== RECEIVED FAXES ===== */}
            <div className={styles.main}>
              <Box sx={{ backgroundColor: 'var(--app-tabs-bg)', mb: 0 }}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <FormHeader items={[{ text: t('faxSettings.receivedFaxesUpper') }]} />

                  {/* View toggle */}
                  <Stack
                    direction='row'
                    alignItems='center'
                    spacing={0.75}
                    onClick={toggleViewMode}
                    sx={{
                      pr: '2rem',
                      cursor: 'pointer',
                      color: 'primary.main',
                      userSelect: 'none',
                    }}
                  >
                    <ViewModule fontSize='small' />
                    <Typography variant='body2' fontWeight={500}>
                      {t('faxSettings.tabView')}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <ReceivedFaxes initialRowsPerPage={5} onFaxSent={onFaxSent} />
            </div>

            {/* ===== SENT FAXES ===== */}
            <div className={styles.main}>
              <Box sx={{ backgroundColor: 'var(--app-tabs-bg)', mb: 0 }}>
                <FormHeader items={[{ text: t('faxSettings.sentFaxesUpper') }]} />
              </Box>

              <SentFaxes initialRowsPerPage={5} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default FaxSettings;
